import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '../../shared/models/api-response';
import { QueryStringHelper } from '../../shared/helpers/query-string.helper';
import { EdaraSetting } from '../../shared/models/edara-setting';
import { Currency, DexieDbProvider, Role, RolesEnum, User } from '@shared';
import { AppSettingsService } from './app-settings.service';
import { Country, State, District, City } from 'src/app/shared/models/customer-v3';
import { DataSourceService } from './data-source.service';
import { Observable, firstValueFrom, map } from 'rxjs';
import { LicenseSubscription } from '@core/models/license-subscription';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  baseUrl = '';
  edaraCoreBaseUrl = '';

  constructor(
    private httpClient: HttpClient,
    private localDbProvider: DexieDbProvider,
    private dataSourceService: DataSourceService,
    private translateService: TranslateService
  ) {
    this.baseUrl = AppSettingsService.appSettings.edaraApi.baseUrl;
    this.edaraCoreBaseUrl = AppSettingsService.appSettings.edaraCoreApi.baseUrl;
    if (this.edaraCoreBaseUrl.endsWith('/')) {
      this.edaraCoreBaseUrl = this.edaraCoreBaseUrl.slice(0, -1);
    }
  }

  async getSettingsByKeyAsync(module: string, key: string) {
    const response = await firstValueFrom(
      this.httpClient.get<ApiResponse<EdaraSetting>>(this.baseUrl + `v2.0/common/settings/FindByKey/${module}?key=` + key)
    ).catch(() => undefined);
    this.addSettingToLocalDb(response?.result);
    return response;
  }

  private addSettingToLocalDb(edaraSetting?: EdaraSetting) {
    if (!this.dataSourceService.canCacheLocally) return;
    if (edaraSetting) {
      this.localDbProvider.db.settings.put(edaraSetting)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  async getSettingsAsync(query: any): Promise<ApiResponse<EdaraSetting[]> | undefined> {
    const queryString = new QueryStringHelper().toQueryString(query);
    return firstValueFrom(
      this.httpClient.get<ApiResponse<EdaraSetting[]>>(this.baseUrl + 'v2.0/common/settings?' + queryString)
    ).catch(() => undefined);
  }

  async getServerDateTimeAsync(): Promise<ApiResponse<Date> | undefined> {
    return firstValueFrom(
      this.httpClient.get<ApiResponse<Date>>(this.baseUrl + 'v2.0/common/GetDateTime')
    ).catch(() => undefined);
  }

  async getSettingByKeyLocalAsync(module: string, key: string): Promise<EdaraSetting | undefined> {
    // Bug 28418: IndexedDB holds the preferred organization's data — read it only there.
    if (!this.dataSourceService.canUseLocalData) return undefined;
    return this.localDbProvider.db.settings
      .where({ module: module, key: key })
      .first((element: EdaraSetting) => {
        return element;
      });
  }

  addSettingsToLocalDb(edaraSettings: EdaraSetting[]) {
    if (!this.dataSourceService.canCacheLocally) return;
    if (edaraSettings && edaraSettings.length > 0) {
      this.localDbProvider.db.settings.bulkPut(edaraSettings)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  //#region Countries

  async getAllCountriesAsync(): Promise<Country[]> {
    let response: Country[];
    const count = await this.countAllCountriesLocalAsync();
    if (this.dataSourceService.useLocalData(count)) {
      response = await this.getCountriesLocalAsync();
      return response;
    } else {
      response = await this.getCountriesAsync() ?? [];
      this.addCountriesToLocalDb(response);
      return response;
    }
  }

  private async countAllCountriesLocalAsync(): Promise<number> {
    return this.localDbProvider.db.countries.count()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private async getCountriesLocalAsync(): Promise<Country[]> {
    return this.localDbProvider.db.countries.toArray()
      .then((countries: Country[]) => this.mapCountriesNameForCurrentLang(countries))
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getCountriesAsync(): Promise<Country[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<Country[]>(this.edaraCoreBaseUrl + '/api/Countries')
        .pipe(map(countries => this.mapCountriesNameForCurrentLang(countries)))
    ).catch(() => undefined);
  }

  private addCountriesToLocalDb(countries: Country[]) {
    if (!this.dataSourceService.canCacheLocally) return;
    if (countries?.length) {
      this.localDbProvider.db.countries.bulkPut(countries)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  private mapCountriesNameForCurrentLang(countries: Country[]): Country[] {
    countries.forEach(country => {
      country.displayName = this.translateService.currentLang === 'en'
        ? country.name
        : country.arName;
    });
    return countries;
  }

  async getCountryByNameLocalAsync(countryName: string): Promise<Country | undefined> {
    if (!countryName) return undefined;

    try {
      const countries = await this.localDbProvider.db.countries
        .where('name').equals(countryName)
        .or('arName').equals(countryName)
        .first();

      if (countries) {
        return this.mapCountriesNameForCurrentLang([countries])[0];
      }
      return undefined;
    } catch (err) {
      this.localDbProvider.handleErrors(err);
      return undefined;
    }
  }

  //#endregion

  //#region States

  async getAllStatesAsync(): Promise<State[]> {
    let response: State[];
    const count = await this.countAllStatesLocalAsync();
    if (this.dataSourceService.useLocalData(count)) {
      response = await this.getStatesLocalAsync();
      return response;
    } else {
      response = await this.getStatesAsync() ?? [];
      this.addStatesToLocalDb(response);
      return response;
    }
  }

  private async countAllStatesLocalAsync(): Promise<number> {
    return this.localDbProvider.db.states.count()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private async getStatesLocalAsync(): Promise<State[]> {
    return this.localDbProvider.db.states.toArray()
      .then((states: State[]) => this.mapStatesNameForCurrentLang(states))
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getStatesAsync(): Promise<State[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<State[]>(this.edaraCoreBaseUrl + '/api/States')
        .pipe(map(states => this.mapStatesNameForCurrentLang(states)))
    ).catch(() => undefined);
  }

  async getStatesByCountryIdAsync(countryId: number): Promise<State[]> {
    let response: State[];
    response = this.dataSourceService.canUseLocalData ? await this.getStatesLocalByCountryIdAsync(countryId) : [];
    if (response?.length) {
      return response;
    } else {
      response = await this.getStatesServerByCountryIdAsync(countryId) ?? [];
      this.addStatesToLocalDb(response);
      return response;
    }
  }

  private async getStatesLocalByCountryIdAsync(countryId: number): Promise<State[]> {
    return this.localDbProvider.db.states.where('countryId').equals(countryId).toArray()
      .then((states: State[]) => this.mapStatesNameForCurrentLang(states))
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getStatesServerByCountryIdAsync(countryId: number): Promise<State[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<State[]>(this.edaraCoreBaseUrl + `/api/States/Find?CountryId=${countryId}`)
        .pipe(map(states => this.mapStatesNameForCurrentLang(states)))
    ).catch(() => undefined);
  }

  private addStatesToLocalDb(states: State[]) {
    if (!this.dataSourceService.canCacheLocally) return;
    if (states?.length) {
      this.localDbProvider.db.states.bulkPut(states)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  private mapStatesNameForCurrentLang(states: State[]): State[] {
    states.forEach(state => {
      state.displayName = this.translateService.currentLang === 'en'
        ? state.name
        : state.arName;

      state.countryDisplayName = this.translateService.currentLang === 'en'
        ? state.countryName
        : state.countryArName;
    });
    return states;
  }

  async getStateByNameLocalAsync(stateName: string, countryName?: string): Promise<State | undefined> {
    if (!stateName) return undefined;

    try {
      let query = this.localDbProvider.db.states
        .where('name').equals(stateName)
        .or('arName').equals(stateName);

      if (countryName) {
        query = query.and((state: State) =>
          state.countryName === countryName || state.countryArName === countryName
        );
      }

      const state = await query.first();

      if (state) {
        return this.mapStatesNameForCurrentLang([state])[0];
      }
      return undefined;
    } catch (err) {
      this.localDbProvider.handleErrors(err);
      return undefined;
    }
  }

  //#endregion

  //#region Cities

  async getCitiesByCountryIdAsync(countryId: number): Promise<City[]> {
    let response: City[];
    // response = await this.getCitiesLocalByCountryIdAsync(countryId);
    // if (response?.length) {
    //   return response;
    // } else {
    response = await this.getCitiesServerByCountryIdAsync(countryId) ?? [];
    this.addCitiesToLocalDb(response);
    return response;
    // }
  }

  private getCitiesServerByCountryIdAsync(countryId: number): Promise<City[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<City[]>(this.edaraCoreBaseUrl + `/api/Cities/Find?CountryId=${countryId}`)
        .pipe(map(cities => this.mapCitiesNameForCurrentLang(cities)))
    ).catch(() => undefined);
  }

  async getCitiesByStateIdAsync(stateId: number): Promise<City[]> {
    let response: City[];
    response = this.dataSourceService.canUseLocalData ? await this.getCitiesLocalByStateIdAsync(stateId) : [];
    if (response?.length) {
      return response;
    } else {
      response = await this.getCitiesServerByStateIdAsync(stateId) ?? [];
      this.addCitiesToLocalDb(response);
      return response;
    }
  }

  private async getCitiesLocalByStateIdAsync(stateId: number): Promise<City[]> {
    return this.localDbProvider.db.cities.where('stateId').equals(stateId).toArray()
      .then((cities: City[]) => this.mapCitiesNameForCurrentLang(cities))
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getCitiesServerByStateIdAsync(stateId: number): Promise<City[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<City[]>(this.edaraCoreBaseUrl + `/api/Cities/Find?StateId=${stateId}`)
        .pipe(map(cities => this.mapCitiesNameForCurrentLang(cities)))
    ).catch(() => undefined);
  }

  private addCitiesToLocalDb(cities: City[]) {
    if (!this.dataSourceService.canCacheLocally) return;
    if (cities?.length) {
      this.localDbProvider.db.cities.bulkPut(cities)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  private mapCitiesNameForCurrentLang(cities: City[]): City[] {
    cities.forEach(city => {
      city.displayName = this.translateService.currentLang === 'en'
        ? city.name
        : city.arName;

      city.stateDisplayName = this.translateService.currentLang === 'en'
        ? city.stateName
        : city.stateArName;

      city.countryDisplayName = this.translateService.currentLang === 'en'
        ? city.countryName
        : city.countryArName;
    });
    return cities;
  }

  async getCityByNameLocalAsync(cityName: string, stateName?: string, countryName?: string): Promise<City | undefined> {
    if (!cityName) return undefined;

    try {
      let query = this.localDbProvider.db.cities
        .where('name').equals(cityName)
        .or('arName').equals(cityName);

      if (stateName) {
        query = query.and((city: City) =>
          city.stateName === stateName || city.stateArName === stateName
        );
      }

      if (countryName) {
        query = query.and((city: City) =>
          city.countryName === countryName || city.countryArName === countryName
        );
      }

      const city = await query.first();

      if (city) {
        return this.mapCitiesNameForCurrentLang([city])[0];
      }
      return undefined;
    } catch (err) {
      this.localDbProvider.handleErrors(err);
      return undefined;
    }
  }

  //#endregion

  //#region Districts

  async getDistrictsByCountryIdAsync(countryId: number): Promise<District[]> {
    let response: District[];
    // response = await this.getDistrictsLocalByCountryIdAsync(countryId);
    // if (response?.length) {
    //   return response;
    // } else {
    response = await this.getDistrictsServerByCountryIdAsync(countryId) ?? [];
    this.addDistrictsToLocalDb(response);
    return response;
    // }
  }

  private getDistrictsServerByCountryIdAsync(countryId: number): Promise<District[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<District[]>(this.edaraCoreBaseUrl + `/api/Districts/Find?CountryId=${countryId}`)
        .pipe(map(districts => this.mapDistrictsNameForCurrentLang(districts)))
    ).catch(() => undefined);
  }

  async getDistrictsByCityIdAsync(cityId: number): Promise<District[]> {
    let response: District[];
    response = this.dataSourceService.canUseLocalData ? await this.getDistrictsLocalByCityIdAsync(cityId) : [];
    if (response?.length) {
      return response;
    } else {
      response = await this.getDistrictsServerByCityIdAsync(cityId) ?? [];
      this.addDistrictsToLocalDb(response);
      return response;
    }
  }

  private async getDistrictsLocalByCityIdAsync(cityId: number): Promise<District[]> {
    return this.localDbProvider.db.districts.where('cityId').equals(cityId).toArray()
      .then((districts: District[]) => this.mapDistrictsNameForCurrentLang(districts))
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private getDistrictsServerByCityIdAsync(cityId: number): Promise<District[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<District[]>(this.edaraCoreBaseUrl + `/api/Districts/Find?CityId=${cityId}`)
        .pipe(map(districts => this.mapDistrictsNameForCurrentLang(districts)))
    ).catch(() => undefined);
  }

  private addDistrictsToLocalDb(districts: District[]) {
    if (!this.dataSourceService.canCacheLocally) return;
    if (districts?.length) {
      this.localDbProvider.db.districts.bulkPut(districts)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  private mapDistrictsNameForCurrentLang(districts: District[]): District[] {
    districts.forEach(district => {
      district.displayName = this.translateService.currentLang === 'en'
        ? district.name
        : district.arName;

      district.cityDisplayName = this.translateService.currentLang === 'en'
        ? district.cityName
        : district.cityArName;

      district.stateDisplayName = this.translateService.currentLang === 'en'
        ? district.stateName
        : district.stateArName;

      district.countryDisplayName = this.translateService.currentLang === 'en'
        ? district.countryName
        : district.countryArName;
    });
    return districts;
  }

  async getDistrictByNameLocalAsync(districtName: string, cityName?: string, stateName?: string, countryName?: string): Promise<District | undefined> {
    if (!districtName) return undefined;

    try {
      let query = this.localDbProvider.db.districts
        .where('name').equals(districtName)
        .or('arName').equals(districtName);

      if (cityName) {
        query = query.and((district: District) =>
          district.cityName === cityName || district.cityArName === cityName
        );
      }

      if (stateName) {
        query = query.and((district: District) =>
          district.stateName === stateName || district.stateArName === stateName
        );
      }

      if (countryName) {
        query = query.and((district: District) =>
          district.countryName === countryName || district.countryArName === countryName
        );
      }

      const district = await query.first();
      if (district) {
        return this.mapDistrictsNameForCurrentLang([district])[0];
      } else {
        return undefined;
      }
    } catch (err) {
      this.localDbProvider.handleErrors(err);
      return undefined;
    }
  }

  //#endregion


  //#region Currencies

  async getAllCurrenciesAsync(): Promise<Currency[]> {
    let response: Currency[];
    const count = await this.countAllCurrenciesLocalAsync();
    if (this.dataSourceService.useLocalData(count)) {
      response = await this.getCurrenciesLocalAsync();
      return response;
    } else {
      response = await this.getCurrenciesAsync() ?? [];
      this.addCurrenciesToLocalDb(response);
      return response;
    }
  }

  private async countAllCurrenciesLocalAsync(): Promise<number> {
    return this.localDbProvider.db.Edara3Currencies.count()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  private async getCurrenciesLocalAsync(): Promise<Currency[]> {
    return this.localDbProvider.db.Edara3Currencies.toArray()
      .catch((err: any) => this.localDbProvider.handleErrors(err));
  }

  getCurrenciesAsync(): Promise<Currency[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<Currency[]>(this.edaraCoreBaseUrl + '/api/Currencies')
    ).catch(() => undefined);
  }

  addCurrenciesToLocalDb(currencies: Currency[]) {
    if (!this.dataSourceService.canCacheLocally) return;
    if (currencies?.length) {
      this.localDbProvider.db.Edara3Currencies.bulkPut(currencies)
        .catch((err: any) => this.localDbProvider.handleErrors(err));
    }
  }

  //#endregion

  getLicenseSubscription(includeCashRegisters: boolean = false): Observable<LicenseSubscription> {
    return this.httpClient.get<LicenseSubscription>(`${this.edaraCoreBaseUrl}/api/Subscription?includeCashRegisters=${includeCashRegisters}`);
  }

  getAllUsersAsync(): Promise<User[] | undefined> {
    return firstValueFrom(
      this.httpClient.get<User[]>(this.edaraCoreBaseUrl + '/api/users/auth')
    ).catch(() => undefined);
  }

  getUsersByRoleAsync(role: RolesEnum, limit: number): Promise<Role | undefined> {
    return firstValueFrom(
      this.httpClient.get<Role>(this.edaraCoreBaseUrl + `/api/users/auth/roles/users?roleName=${role}&limit=${limit}`)
    ).catch(() => undefined);
  }

}
